import { useMemo, SyntheticEvent } from 'react'
import { CheckCircle, Check } from 'lucide-react'
import { cn } from '@utils'
import { formatCurrency } from '@/utils/helpers'
import { getItemImageUrl, setFallbackImageUrl } from '@/utils/image'

interface ExchangeProductProps {
  product: any
  selected: boolean
  original: boolean
  subscriptionOnly?: boolean
  item: any
  onSelect?: (product: any) => void
}

const stripHtml = (html: string): string => {
  if (!html) {
    return ''
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const ExchangeProduct = ({
  product,
  selected,
  original,
  subscriptionOnly = false,
  item,
  onSelect,
}: ExchangeProductProps) => {
  const imageUrl = useMemo(() => {
    const imageItem = {
      campaign_product: product.to_campaign_product,
      variant: product.to_variant,
    }
    return getItemImageUrl(imageItem, 'medium', subscriptionOnly)
  }, [product, subscriptionOnly])

  const variantLabel = useMemo(() => {
    if (!product.to_variant?.options) {
      return ''
    }
    return product.to_variant.options.map((o: any) => `${o.name}: ${o.value}`).join(', ')
  }, [product.to_variant])

  const displayName = useMemo(() => {
    let name = product.to_campaign_product.name || product.to_campaign_product.product?.name || ''
    if (variantLabel) {
      name += ' - ' + variantLabel
    }
    return product.display_name || name
  }, [product, variantLabel])

  const showDiscount = useMemo(() => Number(product.price) < Number(product.retail_price), [product])

  const totalPrice = useMemo(() => {
    const itemVariantId = item?.variant?.id ?? null
    const productVariantId = product.to_variant?.id ?? null
    if (
      original &&
      !subscriptionOnly &&
      item?.campaign_product?.id === product.to_campaign_product?.id &&
      item?.quantity === product.quantity &&
      itemVariantId === productVariantId
    ) {
      return formatCurrency(item.current_price, item.order?.currency)
    }
    return formatCurrency(Number(product.price) * Number(product.quantity), product.to_campaign_product?.currency)
  }, [product, original, subscriptionOnly, item])

  const originalTotalPrice = useMemo(
    () => formatCurrency(Number(product.retail_price) * Number(product.quantity), product.to_campaign_product.currency),
    [product]
  )

  const discount = useMemo(
    () => Math.round(100 - (Number(product.price) / Number(product.retail_price)) * 100),
    [product]
  )

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    const imageItem = {
      campaign_product: product.to_campaign_product,
      variant: product.to_variant,
    }
    setFallbackImageUrl(e.nativeEvent, imageItem, 'medium', true, subscriptionOnly)
  }

  const handleSelect = () => onSelect?.(product)

  const salesPhraseClass = product.sales_phrase?.slug ? `sales-phrase ${product.sales_phrase.slug}` : 'sales-phrase'

  return (
    <div className="exchange-card-wrapper">
      <div
        role="button"
        tabIndex={0}
        className={cn('exchange-card', selected && 'is-selected')}
        onClick={handleSelect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleSelect()
          }
        }}>
        <div className={cn('exchange-check', !selected && 'is-muted')}>
          {selected && <Check size={22} strokeWidth={3} />}
        </div>

        <div className="image">
          <img src={imageUrl} onError={handleImageError} alt="" />
          {product.sales_phrase && (
            <div className={salesPhraseClass} style={product.styles}>
              {product.sales_phrase.text}
            </div>
          )}
        </div>

        <div className="body">
          <div className="name">{displayName}</div>
          <div className="price-row notranslate">
            <span className="price">{totalPrice}</span>
            {showDiscount && <span className="original-price">{originalTotalPrice}</span>}
            {showDiscount && <span className="discount">{discount}% Off</span>}
          </div>
          {product.subtext?.length > 0 && (
            <ul className="benefits">
              {product.subtext.map((text: string, i: number) => (
                <li key={i} className="item">
                  <CheckCircle size={14} className="icon" />
                  <span>{stripHtml(text)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="action">
          <button
            type="button"
            className={cn('btn-base', selected ? 'input-choice-btn' : 'btn-outline-success')}
            onClick={e => {
              e.stopPropagation()
              handleSelect()
            }}>
            {selected ? 'Selected' : original ? 'Keep' : 'Select Package'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExchangeProduct
